import { FundPerformance } from '../../../../fund-performance/dto/FundPerformance';
import * as XLSX from 'xlsx-js-style';
import {
  formats,
  getCell,
  setColumnWidth, withAllColumnsWidth,
  withColumnsWithValuesWidth,
  withHeaderHeight,
  withHeaderStyling,
} from '../../../utils';
import { getColumnsWidths } from '../../portfolio-overview/styles';
import { Row, Rows } from '../../../interfaces';
import { zeroToUndefined } from '../mappings';
import { parseExcelNumber, parseExcelPeriod } from '../../../utils/parsing';
import { Fund } from "../../../../fund-performance/dto/Fund";

export class FpBulkUploadTemplate {
  private headers = ['Period',
    'Total commitment',
    'Contribution received',
    '',
    'Portfolio gain / (loss)',
    'Cumulative OpEx',
    'Management fees',
    'Organizational expenses',
    'Other expenses',
    '',
    'Net Gain (Loss)',
    '',
    'Distribution',
    'NAV (Partners\' capital)',
    'Net IRR (before Carry)',
    'Carried interest',
    'Net IRR (after Carry)'];

  private greenBG: XLSX.CellStyle = {
    fill: {
      patternType: 'solid',
      fgColor: { rgb: 'FDE9D9' },
      bgColor: { rgb: 'FDE9D9' },
    },
  };

  /**
   * Exports excel with {@private headers}.
   *
   * @param fps by a length of 0, will be adjusted with mock data.
   * @param fund for the excel naming.
   */
  export(fps: FundPerformance[], fund: Fund): void {
    const book = XLSX.utils.book_new();
    const sheet = this.getSheet(fps);
    XLSX.utils.book_append_sheet(book, sheet);
    const whiteSpaceIndex = [3, 9, 11];
    whiteSpaceIndex.forEach((index) => setColumnWidth(sheet, index, 4));
    XLSX.writeFile(book, `${fund.name}-fund-performance-bulk-upload.xlsx`);
  }

  import(data: unknown[]): FundPerformance[] {
    const fps: FundPerformance[] = [];
    data.forEach((excelFP) => {
      const fp: FundPerformance = {
        // @ts-expect-error we have to change the fp type from date to LocalDate
        period: parseExcelPeriod(excelFP[this.headers[0]]),
        totalCommitment: parseExcelNumber(excelFP[this.headers[1]], false),
        contribution: parseExcelNumber(excelFP[this.headers[2]] ? String(Math.abs(excelFP[this.headers[2]])) : '0', false),
        totalExpenses: parseExcelNumber(excelFP[this.headers[5]], false),
        mgmFees: parseExcelNumber(excelFP[this.headers[6]], false),
        aggregatedOrgExpenses: parseExcelNumber(excelFP[this.headers[7]], false),
        distribution: parseExcelNumber(String(excelFP[this.headers[12]] ? Math.abs(excelFP[this.headers[12]]) : 0), false),
        netIrr: parseExcelNumber(excelFP[this.headers[14]], true),
        carryInterest: parseExcelNumber(excelFP[this.headers[15]], true),
        netIrrAfterCarry: parseExcelNumber(excelFP[this.headers[16]], true),
        isVisible: true,
      };
      fps.push(fp);
    });
    return fps.filter((fp) => fp.period);
  }

  private getSheet(fps: FundPerformance[]): XLSX.WorkSheet {
    const preparedData = this.prepareFpsForExcel(fps);
    const sheet = XLSX.utils.aoa_to_sheet(preparedData);
    return withColumnsWithValuesWidth(withHeaderHeight(withAllColumnsWidth(withHeaderStyling(sheet))),
      getColumnsWidths());
  }

  private prepareFpsForExcel(fps: FundPerformance[]): Rows {
    if (!fps.length) {
      fps = this.createStubFPs();
    }
    const rows: Rows = [this.headers];
    let rowIndex = 2;
    fps.forEach((fp) => {
      rows.push(this.prepareSingleFP(fp, rowIndex++));
    });
    return rows;
  }

  private prepareSingleFP(fp: FundPerformance, rowIndex: number): Row {
    return [
      fp.period,
      this.getTotalCommitment(fp),
      this.getContribution(fp),
      getCell({}),
      this.getPortfolioGain(fp),
      this.getCumulativeOpex(fp),
      this.getManagementFees(fp),
      this.getOrganizationExpenses(fp),
      this.getOtherExpenses(rowIndex),
      getCell({}),
      this.getNetGainLoss(rowIndex),
      getCell({}),
      this.getDistribution(fp),
      this.getNAV(rowIndex),
      this.getNetIRRBeforeCarry(fp),
      this.getCarriedInterest(fp),
      this.getNetIrrAfterCarry(fp),
    ];
  }

  private getTotalCommitment(fp: FundPerformance): Partial<XLSX.CellObject> {
    return getCell({ v: noValueToZero(fp.totalCommitment), z: formats.thousandsSeparator, t: 'n', s: this.greenBG });
  }

  private getContribution(fp: FundPerformance): Partial<XLSX.CellObject> {
    return getCell({ v: noValueToZero(fp.contribution), z: formats.thousandsSeparator, t: 'n', s: this.greenBG });
  }

  private getPortfolioGain(fp: FundPerformance): Partial<XLSX.CellObject> {
    return getCell({ v: zeroToUndefined(fp.realizedGain + fp.unrealized), z: formats.thousandsSeparator, t: 'n' });
  }

  private getCumulativeOpex(fp: FundPerformance): Partial<XLSX.CellObject> {
    return getCell({
      v: noValueToZero(fp.totalExpenses),
      z: formats.thousandsSeparator, t: 'n',
      s: this.greenBG,
    });
  }

  private getManagementFees(fp: FundPerformance): Partial<XLSX.CellObject> {
    return getCell({
      v: noValueToZero(fp.mgmFees),
      z: formats.thousandsSeparator, t: 'n',
      s: this.greenBG,
    });
  }

  private getOrganizationExpenses(fp: FundPerformance): Partial<XLSX.CellObject> {
    return getCell({
      v: noValueToZero(fp.aggregatedOrgExpenses),
      z: formats.thousandsSeparator, t: 'n',
      s: this.greenBG,
    });
  }

  private getOtherExpenses(rowIndex: number): Partial<XLSX.CellObject> {
    return getCell({
      f: `F${rowIndex}-SUM(G${rowIndex}:H${rowIndex})`,
      z: formats.thousandsSeparator, t: 'n',
    });
  }

  private getNetGainLoss(rowIndex: number): Partial<XLSX.CellObject> {
    return getCell({
      f: `J${rowIndex}-I${rowIndex}-C${rowIndex}`,
      z: formats.thousandsSeparator, t: 'n',
    });
  }

  private getDistribution(fp: FundPerformance): Partial<XLSX.CellObject> {
    return getCell({ v: noValueToZero(-Math.abs(fp.distribution)), z: formats.thousandsSeparator, t: 'n', s: this.greenBG });
  }

  private getNAV(rowIndex: number): Partial<XLSX.CellObject> {
    return getCell({ f: `C${rowIndex}+E${rowIndex}+F${rowIndex}+I${rowIndex}`,
      z: formats.thousandsSeparator, t: 'n' });
  }

  private getNetIRRBeforeCarry(fp: FundPerformance): Partial<XLSX.CellObject> {
    return getCell({ v: noValueToZero(fp.netIrr), z: formats.percentsNoSpace, t: 'n', s: this.greenBG });
  }

  private getCarriedInterest(fp: FundPerformance): Partial<XLSX.CellObject> {
    return getCell({ v: noValueToZero(fp.carryInterest), z: formats.thousandsSeparator, t: 'n', s: this.greenBG });
  }

  private getNetIrrAfterCarry(fp: FundPerformance): Partial<XLSX.CellObject> {
    return getCell({ v: noValueToZero(fp.netIrrAfterCarry), z: formats.percentsNoSpace, t: 'n', s: this.greenBG });
  }

  private createStubFPs(): FundPerformance[] {
    return [FIRST_MOCK_FP, SECOND_MOCK_FP];
  }

}

const noValueToZero = (value: number): number => (value ? value : 0);

const FIRST_MOCK_FP: FundPerformance = {
  // @ts-expect-error we have to change the fp type from date to LocalDate
  period: '2023-03-31',
  totalCommitment: 100000000,
  contribution: 40000000,
  realizedGain: 5000000,
  totalExpenses: -1500000,
  distribution: 2000000,
  netIrr: 0.01,
  carryInterest: -1000000,
  netIrrAfterCarry: 0.08,
};

const SECOND_MOCK_FP: FundPerformance = {
  // @ts-expect-error we have to change the fp type from date to LocalDate
  period: '2022-12-31',
  totalCommitment: 100000000,
  contribution: 35000000,
  realizedGain: 0,
  totalExpenses: -1200000,
  netIrr: -0.05,
};
